import React, { useEffect, useState, useCallback } from 'react'
import GoldSiverHeader from '../HomePage/GoldSiverHeader'
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getPlayerPokemons } from 'store/pokemon';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoint';

function Hospital() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const player_pokemons = useSelector(state => state.pokemon.player_pokemons);
    const [selectedPokemons, setSelectedPokemons] = useState([]);
    const [healingPokemons, setHealingPokemons] = useState({}); // {pokemonId: endTimestamp}
    const [timeRemaining, setTimeRemaining] = useState({}); // {pokemonId: seconds}
    const [isLoading, setIsLoading] = useState(true);

    // Fetch healing status from database
    const fetchHealingStatus = useCallback(async () => {
        try {
            const response = await axiosInstance.get(API_ENDPOINTS.GET_HEALING_STATUS);
            
            console.log('🏥 [Hospital] Healing status response:', response.data);
            
            if (response.data && response.data.success) {
                const healingData = {};
                const remainingData = {};
                
                // Process active healing Pokemon
                if (response.data.healing_pokemons && Array.isArray(response.data.healing_pokemons)) {
                    console.log('🏥 [Hospital] Processing healing Pokemon:', response.data.healing_pokemons);
                    response.data.healing_pokemons.forEach(item => {
                        const pokemonId = parseInt(item.pokemon_id);
                        const endTimestamp = item.healing_end_timestamp * 1000; // Convert to milliseconds
                        const secondsRemaining = parseInt(item.seconds_remaining) || 0;
                        
                        healingData[pokemonId] = endTimestamp;
                        remainingData[pokemonId] = secondsRemaining;
                        
                        console.log(`🏥 [Hospital] Added healing data for Pokemon ${pokemonId}:`, {
                            endTimestamp,
                            secondsRemaining,
                            endTime: new Date(endTimestamp).toLocaleString()
                        });
                    });
                } else {
                    console.log('🏥 [Hospital] No healing Pokemon found in response');
                }
                
                console.log('🏥 [Hospital] Final healing data:', healingData);
                console.log('🏥 [Hospital] Final remaining data:', remainingData);
                
                setHealingPokemons(healingData);
                setTimeRemaining(remainingData);
                
                // If any Pokemon completed healing, refresh the Pokemon list
                if (response.data.completed_pokemons && response.data.completed_pokemons.length > 0) {
                    console.log('🏥 [Hospital] Pokemon completed healing:', response.data.completed_pokemons);
                    // Refresh Pokemon list to get updated HP (all Pokemon - team + box)
                    await dispatch(getPlayerPokemons());
                    // Refresh healing status again to remove completed Pokemon
                    await fetchHealingStatus();
                    toast.success(t('{{count}} Pokemon healed successfully!', { count: response.data.completed_pokemons.length }));
                }
            } else {
                console.error('🏥 [Hospital] API returned success=false:', response.data);
            }
        } catch (error) {
            console.error('🏥 [Hospital] Error fetching healing status:', error);
            console.error('🏥 [Hospital] Error details:', error.response?.data);
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, t]);

    useEffect(() => {
        // Load all Pokemon (team + box) - getPlayerPokemons now returns all Pokemon
        dispatch(getPlayerPokemons());
        fetchHealingStatus();
    }, [dispatch, fetchHealingStatus]);

    // Timer effect for healing countdown
    useEffect(() => {
        if (Object.keys(healingPokemons).length === 0) {
            setTimeRemaining({});
            return; // No Pokemon healing, no need for timer
        }
        
        console.log('🏥 [Hospital] Starting timer for healing Pokemon:', Object.keys(healingPokemons));
        
        const interval = setInterval(() => {
            const now = Date.now();
            const remaining = {};
            let hasCompleted = false;
            
            Object.keys(healingPokemons).forEach(pokemonIdStr => {
                const pokemonId = parseInt(pokemonIdStr);
                const endTime = healingPokemons[pokemonId];
                if (!endTime) return;
                
                const secondsLeft = Math.max(0, Math.floor((endTime - now) / 1000));
                
                if (secondsLeft > 0) {
                    remaining[pokemonId] = secondsLeft;
                } else {
                    // Healing complete - will be handled by server
                    hasCompleted = true;
                    console.log(`🏥 [Hospital] Pokemon ${pokemonId} finished healing (client-side check)`);
                }
            });
            
            setTimeRemaining(remaining);
            
            // If any Pokemon finished healing, fetch status from server immediately
            if (hasCompleted) {
                console.log('🏥 [Hospital] Fetching healing status from server...');
                fetchHealingStatus();
            }
        }, 1000);
        
        return () => clearInterval(interval);
    }, [healingPokemons, fetchHealingStatus]);
    
    // Also check healing status periodically (every 5 seconds) to catch server-side completions
    useEffect(() => {
        if (Object.keys(healingPokemons).length === 0) {
            return;
        }
        
        const statusInterval = setInterval(() => {
            fetchHealingStatus();
        }, 5000); // Check every 5 seconds
        
        return () => clearInterval(statusInterval);
    }, [healingPokemons, fetchHealingStatus]);
    
    // Check healing status when page becomes visible (user returns to page)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && Object.keys(healingPokemons).length > 0) {
                console.log('🏥 [Hospital] Page became visible, refreshing healing status...');
                fetchHealingStatus();
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Also check when component mounts or when user focuses the window
        const handleFocus = () => {
            if (Object.keys(healingPokemons).length > 0) {
                console.log('🏥 [Hospital] Window focused, refreshing healing status...');
                fetchHealingStatus();
            }
        };
        
        window.addEventListener('focus', handleFocus);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [healingPokemons, fetchHealingStatus]);
    
    // Function to manually complete healing for a specific Pokemon
    const handleCompleteHealing = async (pokemonId) => {
        try {
            console.log('🏥 [Hospital] Manually completing healing for Pokemon:', pokemonId);
            const response = await axiosInstance.post(API_ENDPOINTS.COMPLETE_HEALING, {
                pokemon_ids: [pokemonId]
            });
            
            if (response.data && response.data.success) {
                // Refresh healing status and Pokemon list (all Pokemon - team + box)
                await dispatch(getPlayerPokemons());
                await fetchHealingStatus();
                toast.success(t('Pokemon healed successfully!'));
            } else {
                toast.error(response.data?.message || t('Failed to complete healing'));
            }
        } catch (error) {
            console.error('🏥 [Hospital] Error completing healing:', error);
            toast.error(error?.response?.data?.message || t('Failed to complete healing'));
        }
    };

    // Filter Pokemon with low HP from TEAM Pokemon ONLY (opzak = 'ja')
    // Only team Pokemon can be healed at the hospital
    const injuredPokemons = player_pokemons?.filter(pokemon => 
        pokemon && 
        pokemon.opzak === 'ja' && // Only team Pokemon
        pokemon.leven !== undefined && 
        pokemon.levenmax !== undefined && 
        pokemon.leven < pokemon.levenmax
    ) || [];
    
    // Debug logging
    console.log('🏥 [Hospital] Total Pokemon loaded:', {
        player_pokemons_count: player_pokemons?.length || 0,
        team_pokemons_count: player_pokemons?.filter(p => p.opzak === 'ja').length || 0,
        injured_count: injuredPokemons.length,
        pokemon_ids: player_pokemons?.map(p => ({ id: p.id, leven: p.leven, levenmax: p.levenmax, opzak: p.opzak })) || []
    });

    const togglePokemonSelection = (pokemonId) => {
        setSelectedPokemons(prev => {
            if (prev.includes(pokemonId)) {
                return prev.filter(id => id !== pokemonId);
            } else {
                return [...prev, pokemonId];
            }
        });
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleHeal = async () => {
        if (selectedPokemons.length === 0) {
            toast.warning(t('Please select at least one Pokemon to heal'));
            return;
        }

        try {
            console.log('Starting healing for Pokemon:', selectedPokemons);
            const response = await axiosInstance.post(API_ENDPOINTS.HEAL_POKEMON, {
                pokemon_ids: selectedPokemons
            });

            if (response.data && response.data.success) {
                // Refresh healing status from server
                await fetchHealingStatus();
                
                // Use healing time from response (in minutes) or calculate from seconds
                const healingTimeMinutes = response.data.healing_time_minutes || (response.data.healing_time_seconds / 60) || 3;
                const totalMinutes = Math.round(response.data.pokemon_count * healingTimeMinutes);
                toast.success(t('Healing started! Time remaining: {{minutes}} minutes', { minutes: totalMinutes }));
                
                setSelectedPokemons([]);
            } else {
                toast.error(response.data?.message || t('Failed to start healing'));
            }
        } catch (error) {
            console.error('Error healing Pokemon:', error);
            toast.error(error?.response?.data?.message || t('Failed to start healing'));
        }
    };

    return (
        <div>
            <GoldSiverHeader previous={'/city'} title='Hospital'>
                <section className="ar_hospital_area_section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_hospital_area">
                                    <img src="/images/hospital/hospital-bg.png" alt="" />
                                    <div className="ar_hospital_recovery">
                                        <img src="/images/hospital/recovery.png" alt="" />
                                    </div>
                                    <div className="ar_hotpita_btnCoin">
                                        <a 
                                            href="#" 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleHeal();
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <img src="/images/hospital/hosbtn.png" alt={t('Heal')} />
                                        </a>
                                        <a href="#" onClick={(e) => e.preventDefault()}><img src="/images/hospital/btnplace.png" alt="" /></a>
                                    </div>
                                    <div className="ar_hospital_item_single">
                                        {isLoading ? (
                                            <div className="text-center text-white p-4">
                                                <p>{t('Loading...')}</p>
                                            </div>
                                        ) : injuredPokemons.length === 0 ? (
                                            <div className="text-center text-white p-4">
                                                <p>{t('All Pokemon are healthy!')}</p>
                                            </div>
                                        ) : (
                                            injuredPokemons.map((item) => {
                                                const isSelected = selectedPokemons.includes(item.id);
                                                const pokemonId = parseInt(item.id);
                                                const healingEndTime = healingPokemons[pokemonId];
                                                // Check if healing is complete (time passed but not yet processed)
                                                const timeLeft = timeRemaining[pokemonId] || 0;
                                                const isHealing = healingEndTime && (Date.now() < healingEndTime || timeLeft > 0);
                                                const isHealingComplete = healingEndTime && Date.now() >= healingEndTime && timeLeft <= 0;
                                                const hpPercentage = (item.leven / item.levenmax) * 100;
                                                
                                                // Debug logging
                                                if (healingEndTime) {
                                                    console.log(`🏥 [Hospital] Pokemon ${pokemonId} healing status:`, {
                                                        isHealing,
                                                        healingEndTime,
                                                        currentTime: Date.now(),
                                                        timeLeft,
                                                        endTime: new Date(healingEndTime).toLocaleString()
                                                    });
                                                }
                                                
                                                return (
                                                    <div 
                                                        key={item.id}
                                                        className='position-relative cursor-pointer' 
                                                        style={{
                                                            marginRight: '10px',
                                                            opacity: isHealing ? 0.6 : 1,
                                                            border: isSelected ? '3px solid #4CAF50' : '3px solid transparent',
                                                            borderRadius: '10px',
                                                            padding: isSelected ? '3px' : '0'
                                                        }}
                                                        onClick={() => !isHealing && togglePokemonSelection(item.id)}
                                                    >
                                                        <img src="/images/hospital/ho2.png" alt="" className='m-0'/>
                                                        <img 
                                                            src={`/images/pokemon/${item.wild_id}.gif`} 
                                                            alt="" 
                                                            height={90} 
                                                            className='position-absolute pokemon-img' 
                                                            style={{
                                                                top: '50%',
                                                                right: '50%',
                                                                transform: 'translate(50%,-50%)',
                                                                margin: '0px'
                                                            }}
                                                        />
                                                        {isSelected && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '5px',
                                                                right: '5px',
                                                                background: '#4CAF50',
                                                                borderRadius: '50%',
                                                                width: '25px',
                                                                height: '25px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: 'white',
                                                                fontWeight: 'bold',
                                                                fontSize: '16px'
                                                            }}>
                                                                ✓
                                                            </div>
                                                        )}
                                                        {isHealing && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                bottom: '5px',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                background: 'rgba(0,0,0,0.9)',
                                                                color: '#fff',
                                                                padding: '8px 12px',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                fontWeight: 'bold',
                                                                zIndex: 10,
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.7)',
                                                                minWidth: '120px',
                                                                textAlign: 'center',
                                                                border: '2px solid #4CAF50'
                                                            }}>
                                                                {timeLeft > 0 ? (
                                                                    <div>
                                                                        <div>{t('Healing...')}</div>
                                                                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                                                            {formatTime(timeLeft)}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div>
                                                                        <div style={{ marginBottom: '8px' }}>{t('Healing complete!')}</div>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                handleCompleteHealing(pokemonId);
                                                                            }}
                                                                            style={{
                                                                                background: '#4CAF50',
                                                                                color: 'white',
                                                                                border: 'none',
                                                                                borderRadius: '4px',
                                                                                padding: '4px 12px',
                                                                                fontSize: '12px',
                                                                                cursor: 'pointer',
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            {t('Complete')}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: isHealing ? '30px' : '5px',
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: '80%',
                                                            height: '8px',
                                                            background: 'rgba(0,0,0,0.5)',
                                                            borderRadius: '4px',
                                                            overflow: 'hidden'
                                                        }}>
                                                            <div style={{
                                                                width: `${hpPercentage}%`,
                                                                height: '100%',
                                                                background: hpPercentage < 25 ? '#f44336' : hpPercentage < 50 ? '#ff9800' : '#4CAF50',
                                                                transition: 'width 0.3s'
                                                            }}></div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </GoldSiverHeader>
        </div>
    )
}

export default Hospital
